import React, {useState, useRef} from 'react';

interface SkillsInputProps {
  value: string[];
  onChange: (skills: string[]) => void;
  placeholder?: string;
  maxSkills?: number;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

export const SkillsInput: React.FC<SkillsInputProps> = ({
  value = [],
  onChange,
  placeholder = 'E.g., Data analytics, Project management, Sales, ReactJS',
  maxSkills = 30,
  error = false,
  disabled = false,
  className = '',
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addSkill = (skill: string) => {
    if (skill.trim() && !value.includes(skill.trim()) && value.length < maxSkills) {
      onChange([...value, skill.trim()]);
      setInputValue('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    onChange(value.filter(skill => skill !== skillToRemove));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addSkill(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeSkill(value[value.length - 1]);
    }
  };

  const borderColor = error
    ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-200'
    : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-blue-200';

  return (
    <div className={`relative ${className}`}>
      <div
        className={`min-h-[48px] w-full px-4 py-3 border rounded-lg bg-white outline-none transition-all duration-150 ease-in-out ${borderColor} focus-within:ring-2 ${
          disabled ? 'opacity-60 pointer-events-none bg-gray-50' : ''
        }`}
        onClick={() => inputRef.current?.focus()}>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Render selected skills as tags */}
          {value.map((skill, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full"
              style={{
                backgroundColor: '#EAEFFA',
                color: '#2E7CF6',
              }}>
              {skill}
              {!disabled && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    removeSkill(skill);
                  }}
                  className="ml-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-medium hover:opacity-80"
                  style={{
                    backgroundColor: '#2E7CF6',
                    color: '#FFFFFF',
                  }}
                  aria-label={`Remove ${skill}`}>
                  ×
                </button>
              )}
            </span>
          ))}

          {/* Input field */}
          {value.length < maxSkills && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={value.length === 0 ? placeholder : ''}
              className="flex-1 min-w-[200px] outline-none bg-transparent text-gray-900 placeholder-gray-500"
              disabled={disabled}
            />
          )}

          {/* Add skill button */}
          {inputValue.trim() && (
            <button
              type="button"
              onClick={() => addSkill(inputValue)}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1">
              <span className="text-lg">+</span> Add skill
            </button>
          )}
        </div>
      </div>

      {/* Skills counter */}
      <div className="mt-2 text-xs text-gray-500">
        You can add up to {maxSkills} skills and areas of expertise and can update these at any time.
      </div>
    </div>
  );
};
